import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

interface KeyHistoryEntry {
  id: string;
  tenant_id: string | null;
  key_version: number;
  key_hash: string;
  rotated_at: string;
  rotated_by: string | null;
  rotated_by_email: string | null;
  reason: string | null;
  is_active: boolean;
  created_at: string;
}

interface KeyHistoryTableProps {
  history: KeyHistoryEntry[];
  isLoading?: boolean;
}

function maskHash(hash: string): string {
  if (!hash || hash.length < 12) return hash;
  return `${hash.substring(0, 8)}...${hash.substring(hash.length - 4)}`;
}

export function KeyHistoryTable({ history, isLoading = false }: KeyHistoryTableProps) {
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No key rotation history found
      </div>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-3">
        {history.map((entry) => (
          <Card key={entry.id}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium">Version {entry.key_version}</span>
                {entry.is_active ? (
                  <Badge variant="default">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                  {maskHash(entry.key_hash)}
                </code>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Rotated:</span>{" "}
                {format(new Date(entry.rotated_at), "MMM d, yyyy h:mm a")}
              </div>
              {entry.rotated_by_email && (
                <div className="text-sm">
                  <span className="text-muted-foreground">By:</span> {entry.rotated_by_email}
                </div>
              )}
              {entry.reason && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Reason:</span> {entry.reason}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">Version</TableHead>
            <TableHead>Key Hash</TableHead>
            <TableHead>Rotated At</TableHead>
            <TableHead>Rotated By</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="w-20">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="font-medium">{entry.key_version}</TableCell>
              <TableCell>
                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                  {maskHash(entry.key_hash)}
                </code>
              </TableCell>
              <TableCell>{format(new Date(entry.rotated_at), "MMM d, yyyy h:mm a")}</TableCell>
              <TableCell>{entry.rotated_by_email || "-"}</TableCell>
              <TableCell className="max-w-[200px] truncate">{entry.reason || "-"}</TableCell>
              <TableCell>
                {entry.is_active ? (
                  <Badge variant="default">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
